import { FC, useEffect, useRef, useState } from "react"
import { observer } from "mobx-react"
import { useStore } from "stores/utils/use-store"
import { Button, Card, Form, Popconfirm, Space, Tag, Typography } from "antd"
import { Contract, GameplayCutConfig } from "../store/types"
import { GameplayCutFormValues, formToGameplayCut, resolveGameplayCut } from "../gameplay-cut"
import { seedForm } from "../utils"
import { GameplayCutFields } from "./gameplay-cut-fields"

export const GameplayCutSettings: FC<{ contract: Contract }> = observer(({ contract }) => {
  const { dataStore: { dataDeliveryStore: store } } = useStore()
  const [form] = Form.useForm<GameplayCutFormValues>()
  const [saving, setSaving] = useState(false)

  const seededFor = useRef<Pick<Contract, "id" | "updatedAt"> | null>(null)

  useEffect(() => {
    const seeded = seededFor.current
    if (seeded?.id === contract.id && (seeded.updatedAt === contract.updatedAt || form.isFieldsTouched())) return
    seededFor.current = { id: contract.id, updatedAt: contract.updatedAt }
    seedForm(form, resolveGameplayCut(contract.gameplayCut))
  }, [form, contract])

  const save = async(gameplayCut: GameplayCutConfig | null) => {
    setSaving(true)
    const updated = await store.updateContract(contract.id, { gameplayCut })
    setSaving(false)
    if (updated) {
      seedForm(form, resolveGameplayCut(updated.gameplayCut))
    }
  }

  const onSave = async() => {
    let values: GameplayCutFormValues
    try {
      values = await form.validateFields()
    } catch {
      return
    }
    await save(formToGameplayCut(values))
  }

  const onClear = () => save(null)

  return (
    <Card
      size="small"
      title={(
        <Space>
          Gameplay cut
          {contract.gameplayCut ? <Tag color="green">configured</Tag> : <Tag>not set</Tag>}
        </Space>
      )}
      extra={(
        <Space>
          <Popconfirm
            title="Clear the gameplay cut for this contract?"
            onConfirm={onClear}
            disabled={!contract.gameplayCut}
          >
            <Button size="small" danger disabled={!contract.gameplayCut} loading={saving}>Clear</Button>
          </Popconfirm>
          <Button type="primary" size="small" loading={saving} onClick={onSave}>Save gameplay cut</Button>
        </Space>
      )}
    >
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        When set, the gameplay cut overrides the legacy cut mode, min players to cut and any per-game entitlement cut
        overrides; only the contract pipeline version still applies. Fields show the profile preset until overridden;
        only overrides are stored.
      </Typography.Paragraph>
      <Form form={form} layout="vertical" size="small">
        <GameplayCutFields form={form} />
      </Form>
    </Card>
  )
})
